/*!
 * Copyright (c) Microsoft Corporation and contributors. All rights reserved.
 * Licensed under the MIT License.
 */
import { assert } from "@fluidframework/core-utils/internal";
import { DataProcessingError } from "@fluidframework/telemetry-utils/internal";
const stashedOpMetadataMark = Symbol();
function createStashedOpMetadata() {
    const arr = [];
    Object.defineProperty(arr, stashedOpMetadataMark, {
        value: stashedOpMetadataMark,
        writable: false,
        enumerable: true,
    });
    return arr;
}
function isStashedOpMetadata(md) {
    return (Array.isArray(md) &&
        stashedOpMetadataMark in md &&
        md[stashedOpMetadataMark] === stashedOpMetadataMark);
}
function processWithStashedOpMetadataHandling(content, localOpMetaData, func) {
    if (isStashedOpMetadata(localOpMetaData)) {
        localOpMetaData.forEach(({ contents, metadata }) => func(contents, metadata));
    }
    else {
        func(content, localOpMetaData);
    }
}
export class ChannelDeltaConnection {
    get handler() {
        assert(!!this._handler, 0x177 /* "Missing delta handler" */);
        return this._handler;
    }
    get connected() {
        return this._connected;
    }
    constructor(_connected, submitFn, dirty, isAttachedAndVisible) {
        this._connected = _connected;
        this.submitFn = submitFn;
        this.dirty = dirty;
        this.isAttachedAndVisible = isAttachedAndVisible;
    }
    attach(handler) {
        assert(this._handler === undefined, 0x178 /* "Missing delta handler on attach" */);
        this._handler = handler;
    }
    setConnectionState(connected) {
        this._connected = connected;
        this.handler.setConnectionState(connected);
    }
    process(message, local, localOpMetadata) {
        try {
            // catches as data processing error whether or not they come from async pending queues
            processWithStashedOpMetadataHandling(message.contents, localOpMetadata, (contents, metadata) => this.handler.process({ ...message, contents }, local, metadata));
        }
        catch (error) {
            throw DataProcessingError.wrapIfUnrecognized(error, "channelDeltaConnectionFailedToProcessMessage", message);
        }
    }
    reSubmit(content, localOpMetadata) {
        processWithStashedOpMetadataHandling(content, localOpMetadata, this.handler.reSubmit.bind(this.handler));
    }
    rollback(content, localOpMetadata) {
        if (this.handler.rollback === undefined) {
            throw new Error("Handler doesn't support rollback");
        }
        processWithStashedOpMetadataHandling(content, localOpMetadata, this.handler.rollback.bind(this.handler));
    }
    applyStashedOp(content) {
        try {
            this.stashedOpMd = this.isAttachedAndVisible() ? createStashedOpMetadata() : undefined;
            this.handler.applyStashedOp(content);
            return this.stashedOpMd;
        }
        finally {
            this.stashedOpMd = undefined;
        }
    }
    submit(contents, metadata) {
        if (this.stashedOpMd !== undefined) {
            this.stashedOpMd.push({ contents, metadata });
        }
        else {
            this.submitFn(contents, metadata);
        }
    }
}
//# sourceMappingURL=channelDeltaConnection.js.map